import cx from 'classnames'
import styles from './DocumentThumbnail.module.css'
import { Skeleton, useIsSkeleton } from '@/components/Skeleton'
import { Icon } from '@/components/Icon'
import { useCallback, useRef, useState } from 'react'

export interface DocumentTypeThumbnailProps {
  imageUrl?: string | null
  isSkeleton?: boolean
  name?: string | null
  testId?: string
}

/**
 * A thumbnail for a document.
 */
export function DocumentThumbnail({
  name,
  imageUrl,
  isSkeleton,
  testId,
}: DocumentTypeThumbnailProps) {
  const prevImageUrl = useRef(imageUrl)
  const [loaded, setLoaded] = useState(false)
  const onLoad = useCallback(
    () => requestAnimationFrame(() => setLoaded(true)),
    [],
  )

  if (prevImageUrl.current !== imageUrl) {
    prevImageUrl.current = imageUrl
    setLoaded(false)
  }

  isSkeleton = useIsSkeleton(isSkeleton)
  isSkeleton ||= Boolean(imageUrl) && !loaded

  return (
    <div
      aria-hidden
      data-test={testId}
      className={cx(
        styles.root,
        isSkeleton && styles.skeleton,
        imageUrl && styles.thumbnail,
      )}
    >
      {isSkeleton ? (
        <Skeleton
          variant="rect"
          width="var(--document-type-thumbnail-size)"
          height="var(--document-type-thumbnail-size)"
        />
      ) : (
        !imageUrl && <Icon variant="secondary" name="file" sm />
      )}

      {imageUrl && (!isSkeleton || !loaded) && (
        <img
          className={cx(styles.image, !loaded && styles.hidden)}
          src={imageUrl}
          alt={name ?? undefined}
          onLoad={onLoad}
        />
      )}
    </div>
  )
}
